import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import debounce from 'lodash/debounce';
import Modal from '../components/Modal';
import QuickSettingsModal from '../components/QuickSettingsModal';
// import './AddMediaPage.css';

// Maps the AddMediaPage's own lowercase `mediaType` values to the backend MediaItem.type used by
// getStatusOptions/getOwnershipOptions and the /update_quick_setting endpoint.
const BACKEND_TYPE_MAP = {
  anime: 'Anime',
  manga: 'Manga',
  vinyl: 'Music',
  cd: 'Music',
  steam: 'SteamGame',
  psp: 'PhysicalGame',
  vita: 'PhysicalGame',
  nds: 'PhysicalGame',
  switch: 'PhysicalGame',
  movie: 'ScreenMedia',
  tv: 'ScreenMedia',
  book: 'Book',
};

const AddMediaPage = ({ apiBaseUrl }) => {
  const { isAuthenticated, userRoles } = useAuth();
  const [mediaType, setMediaType] = useState('anime');
  const [query, setQuery] = useState('');
  const [manualId, setManualId] = useState('');
  const [results, setResults] = useState([]);

  const [malResults, setMalResults] = useState([]);
  const [mangadexResults, setMangadexResults] = useState([]);


  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', buttons: [], inputConfig: null });
  const [quickSettings, setQuickSettings] = useState({ isOpen: false, mediaId: null, mediaType: null, title: '' });

  const closeQuickSettings = () => setQuickSettings((prev) => ({ ...prev, isOpen: false }));

  const mediaTypes = [
    { value: 'anime', label: 'Anime' },
    { value: 'manga', label: 'Manga' },
    //rewmoved { value: 'manga-alt', label: 'Manga (mangadex)'},
    { value: 'vinyl', label: 'Vinyl' },
    { value: 'cd', label: 'CD' },
    { value: 'steam', label: 'Steam Game' },
    { value: 'psp', label: 'PSP Game' },
    { value: 'vita', label: 'Vita Game' },
    { value: 'nds', label: 'NDS Game' },
    { value: 'switch', label: 'Switch Game' },
    { value: 'movie', label: 'Movies'},
    { value: 'tv', label: 'TV Series'},
    { value: 'book', label: 'Books & Comics'},
  ];

  const searchMedia = debounce(async () => {
    if (query.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
		if (mediaType === 'manga' && userRoles.includes('manage-media')) {
			const [malRes, mdexRes] = await Promise.all([
				fetch(`${apiBaseUrl}/add/search`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					credentials: 'include',
					body: JSON.stringify({ media_type: 'manga', query }),
				}),
				fetch(`${apiBaseUrl}/add/search`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					credentials: 'include',
					body: JSON.stringify({ media_type: 'manga-alt', query }),
				}),
			]);

			const malData = await malRes.json();
			const mdexData = await mdexRes.json();

			setMalResults(malData.error ? [{ error: malData.error }] : malData);
			setMangadexResults(mdexData.error ? [{ error: mdexData.error }] : mdexData);
			setResults([]); // clear general results
		} else {
			const res = await fetch(`${apiBaseUrl}/add/search`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({ media_type: mediaType, query }),
			});
			const data = await res.json();
			setResults(data.error ? [{ error: data.error }] : data);
			setMalResults([]);
			setMangadexResults([]);
		}
	} catch (err) {
      console.error('Search error:', err);
      setResults([{ error: 'Failed to search' }]);
    } finally {
      setLoading(false);
    }
  }, 400);

  useEffect(() => {
    searchMedia();
    return () => searchMedia.cancel();
  }, [query, mediaType]);

  const showModal = (title, message, buttons, inputConfig = null) => {
    setModal({ isOpen: true, title, message, buttons, inputConfig });
  };

  const closeModal = () => setModal({ ...modal, isOpen: false });

  const handleAdd = async (id, title, image, source) => {
    const canManage = isAuthenticated && userRoles.includes('manage-media');
    const action = canManage ? 'Add' : 'Recommend';

    const buttons = [
      {
        label: 'Yes',
        class: 'btn btn-primary',
        action: async (input) => await performAdd(id, title, image, canManage, input, source),
      },
      { label: 'Cancel', class: 'btn btn-secondary', action: () => {} },
    ];
    const inputConfig = canManage
      ? null
      : {
          label: 'Want to let them know who suggested this?',
          placeholder: 'Your name or nickname (optional)',
          class: 'input-field',
        };

    showModal(
      `${action} Media`,
      `${action} "${title}" to ${action.toLowerCase() === 'recommend' ? 'be added' : 'your list'}?`,
      buttons,
      inputConfig
    );
  };

  const performAdd = async (id, title, image, canManage, submitted_by, source) => {
    try {
      const payload = canManage
        ? { title, image }
        : { title, image, submitted_by: submitted_by?.trim() || undefined };
      // Use 'manga-alt' for MangaDex items, otherwise use mediaType
      const effectiveMediaType = source === 'mangadex' ? 'manga-alt' : mediaType;
      const res = await fetch(`${apiBaseUrl}/add/${effectiveMediaType}/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        if (canManage && data.media_id) {
          setQuickSettings({
            isOpen: true,
            mediaId: data.media_id,
            mediaType: BACKEND_TYPE_MAP[effectiveMediaType] || BACKEND_TYPE_MAP[mediaType],
            title,
          });
        } else {
          showModal(
            'Success',
            'Recommended successfully!',
            [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
            null,
            <img src={data.icon} alt="Success" className="modal-icon" />
          );
        }
      } else if (res.status === 409) {
        showModal(
          'Warning',
          `${canManage ? 'Add' : 'Recommendation'} already exists`,
          [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
          null,
          <img src={data.icon} alt="Warning" className="modal-icon" />
        );
      } else {
        showModal(
          'Error',
          `${canManage ? 'Add' : 'Recommendation'} failed`,
          [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
          null,
          <img src={data.icon} alt="Error" className="modal-icon" />
        );
        console.error(data);
      }
    } catch (err) {
      showModal(
        'Error',
        `${canManage ? 'Add' : 'Recommendation'} failed`,
        [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
        null,
        <img src="/static/icons/xmark-circle-solid.svg" alt="Error" className="modal-icon" />
      );
      console.error(err);
    }
  };

  const handleAddById = async () => {
    if (!manualId) {
      showModal('Warning', 'Please enter an ID', [
        { label: 'OK', class: 'btn btn-primary', action: () => {} },
      ], null, <img src="/static/icons/exclamation-circle-solid.svg" alt="Warning" className="modal-icon" />);
      return;
    }

    showModal('Confirm', `Add media with ID ${manualId} as ${mediaType}?`, [
      { label: 'Yes', class: 'btn btn-primary', action: async () => await performAddById() },
      { label: 'Cancel', class: 'btn btn-secondary', action: () => {} },
    ]);
  };

  const performAddById = async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/add/${mediaType}/${manualId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        if (data.media_id) {
          setQuickSettings({
            isOpen: true,
            mediaId: data.media_id,
            mediaType: BACKEND_TYPE_MAP[mediaType],
            title: '',
          });
        } else {
          showModal('Success', 'Added successfully!', [
            { label: 'OK', class: 'btn btn-primary', action: () => {} },
          ], null, <img src={data.icon} alt="Success" className="modal-icon" />);
        }
      } else if (res.status === 409) {
        showModal('Warning', 'Already exists', [
          { label: 'OK', class: 'btn btn-primary', action: () => {} },
        ], null, <img src={data.icon} alt="Warning" className="modal-icon" />);
      } else {
        showModal('Error', 'Failed to add', [
          { label: 'OK', class: 'btn btn-primary', action: () => {} },
        ], null, <img src={data.icon} alt="Error" className="modal-icon" />);
        console.error(data);
      }
    } catch (err) {
      showModal('Error', 'Failed to add', [
        { label: 'OK', class: 'btn btn-primary', action: () => {} },
      ], null, <img src="/static/icons/xmark-circle-solid.svg" alt="Error" className="modal-icon" />);
      console.error(err);
    }
  };

  const MediaCard = ({ item, onAdd }) => (
    <div className="media-poster-card" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flexGrow: 1 }}>
        <img
          src={item.image}
          className="poster-img"
          style={{ width: '100%', height: '220px', objectFit: 'contain', borderBottom: '1px solid rgba(255,255,255,0.1)' }}
          alt={item.title}
        />
        <div className="poster-info" style={{ padding: '8px', minHeight: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <strong>{item.title}</strong>
        </div>
      </div>
      <div style={{ padding: '10px' }}>
        <button
          className="btn btn-secondary"
          style={{ width: '100%' }}
          onClick={() => onAdd(item.id, item.title, item.image)}
        >
          Add
        </button>
      </div>
    </div>
  );


  return (
    <div className="home-container">
      <h1 style={{ marginBottom: '20px', textAlign: 'center' }}>Add New Media</h1>
      <div className="search-section">
        <div className="search-input-wrapper" style={{ maxWidth: '300px', margin: '0 auto 15px auto' }}>
          <select
            id="media_type"
            className="input-field"
            value={mediaType}
            onChange={(e) => setMediaType(e.target.value)}
            style={{ paddingRight: '40px' }}
          >
            {mediaTypes.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <img
            src="/static/icons/caret-down-solid.svg"
            className="icon"
            style={{ position: 'absolute', top: '50%', right: '18px', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            alt="Dropdown"
          />
        </div>
        <div className="search-input-wrapper" style={{ marginBottom: 0 }}>
          <input
            type="text"
            id="query"
            placeholder="Search by title or ID..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <img
            src="/static/icons/magnifying-glass-solid.svg"
            className="icon"
            style={{ position: 'absolute', top: '50%', left: '18px', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            alt="Search"
          />
        </div>
        {isAuthenticated && (
          <div style={{ textAlign: 'center', marginTop: '6px', fontSize: '13px', color: 'var(--text-muted)' }}>
            Add manually by ID:
            <input
              type="text"
              id="manual_id"
              placeholder="e.g. 5114"
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: 'none', background: 'var(--background-accent)', color: 'var(--text-color)', width: '100px', marginLeft: '5px' }}
            />
            <button onClick={handleAddById} className="btn btn-secondary" style={{ fontSize: '13px', padding: '6px 10px', borderRadius: '6px', marginLeft: '5px' }}>
              Go
            </button>
          </div>
        )}
      </div>
      {loading && <p style={{ textAlign: 'center' }}>Loading...</p>}

      {mediaType === 'manga' && userRoles.includes('manage-media') ? (
        <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '30px' }}>
          <div style={{ flex: 1, padding: '0 10px' }}>
            <h3 style={{ textAlign: 'center' }}>MyAnimeList</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center' }}>
              {malResults.length === 0 && !loading && query.length >= 2 && <p>No MAL results found.</p>}
              {malResults.map((item, index) => (
                item.error ? (
                  <p key={index} style={{ color: 'red' }}>{item.error}</p>
                ) : (
                  <MediaCard
                    key={item.id}
                    item={item}
                    onAdd={() => handleAdd(item.id, item.title, item.image, 'mal')}
                  />
                )
              ))}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 10px' }}>
            <h3 style={{ textAlign: 'center' }}>MangaDex</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center' }}>
              {mangadexResults.length === 0 && !loading && query.length >= 2 && <p>No MangaDex results found.</p>}
              {mangadexResults.map((item, index) => (
                item.error ? (
                  <p key={index} style={{ color: 'red' }}>{item.error}</p>
                ) : (
                  <MediaCard
                    key={item.id}
                    item={item}
                    onAdd={() => handleAdd(item.id, item.title, item.image, 'mangadex')}
                  />
                )
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div id="results" style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center', marginTop: '30px' }}>
          {results.length === 0 && !loading && query.length >= 2 && <p>No results found.</p>}
          {results.map((item, index) => (
            item.error ? (
              <p key={index} style={{ color: 'red' }}>{item.error}</p>
            ) : (
              <MediaCard key={item.id} item={item} onAdd={() => handleAdd(item.id, item.title, item.image, mediaType)} />
            )
          ))}
        </div>
      )}

      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        buttons={modal.buttons}
        inputConfig={modal.inputConfig}
      />
      <QuickSettingsModal
        isOpen={quickSettings.isOpen}
        onClose={closeQuickSettings}
        mediaId={quickSettings.mediaId}
        mediaType={quickSettings.mediaType}
        apiBaseUrl={apiBaseUrl}
        title={quickSettings.title}
      />
    </div>
  );
};

export default AddMediaPage;